import React from 'react'

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@digital-twins/design-system/components/ui/tooltip'

/**
 * The (i) beside a chart label whose whole value the chart does not show
 * (plots plan C2 decision 21, LB decision 24): the label is cut by its clamp
 * (an ellipsis), or the chart prints a label other than its source text. On
 * every interactive surface the whole value is one hover or one keyboard focus
 * away.
 *
 * Never drawn in a static render (the PDF pass): no pointer or focus ever
 * arrives there, so a trigger would be dead markup, and the PDF keeps the
 * ellipsis. A label printed whole draws nothing, so a chart whose labels all
 * fit is unchanged.
 */

export type ChartLabelRenderMode = 'interactive' | 'static'

export interface ChartLabelFullTextProps {
  /** The text the chart prints. */
  label: string
  /** The source text (`fullLabel` on the payload); absent when the label is the source. */
  fullText?: string | null
  /** The clamp cuts the printed label (an ellipsis): its whole value is shown. */
  truncated?: boolean
  /** Position among the plot's hints: `chart-label-full-text-{index}`. */
  index: number
  renderMode?: ChartLabelRenderMode
  /** Pixel size of the (i) glyph. */
  size?: number
  /**
   * A note about the label (a scorecard dimension's construct line, SC-f2): the (i) is then
   * always drawn and its tooltip carries the note under the whole value, if any.
   */
  description?: string | null
  /** The trigger's testid when it is not one of a plot's indexed hints (default `chart-label-full-text-{index}`). */
  testId?: string
  /** The trigger's accessible name when it is not the whole value and note (default). */
  ariaLabel?: string
}

/** The source text to show for a printed label, or null when there is nothing to reveal. */
export function chartLabelFullText(label: string, fullText?: string | null): string | null {
  const full = (fullText ?? '').trim()
  if (!full || full === label.trim()) {
    return null
  }
  return full
}

/**
 * Hint positions for a categorical plot: row names first, then the categories,
 * so a row and a category never share an index.
 */
export function chartLabelHintIndex(rowCount: number, role: 'row' | 'category', index: number): number {
  return role === 'row' ? index : rowCount + index
}

export function ChartLabelFullText(props: ChartLabelFullTextProps) {
  const { label, fullText, truncated = false, index, renderMode = 'interactive', size = 12, description, testId, ariaLabel } = props

  const full = chartLabelFullText(label, fullText) ?? (truncated && label.trim() ? label.trim() : null)
  const note = description?.trim() || null

  if ((!full && !note) || renderMode === 'static') {
    return null
  }

  const triggerTestId = testId ?? `chart-label-full-text-${index}`

  const content = note ? (
    <>
      {full ? <div>{full}</div> : null}
      <div data-testid={`${triggerTestId}-description`} style={full ? { marginTop: 4 } : undefined}>
        {note}
      </div>
    </>
  ) : (
    full
  )

  return (
    <TooltipProvider>
      <Tooltip delayDuration={150}>
        <TooltipTrigger
          type="button"
          aria-label={ariaLabel ?? [full, note].filter(Boolean).join('. ')}
          data-testid={triggerTestId}
          onClick={(event) => event.stopPropagation()}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            width: size,
            height: size,
            padding: 0,
            margin: 0,
            border: '1px solid currentColor',
            borderRadius: '50%',
            background: 'transparent',
            color: 'var(--slide-text-muted)',
            fontSize: Math.round(size * 0.7),
            fontStyle: 'italic',
            fontWeight: 600,
            lineHeight: 1,
            cursor: 'help',
            pointerEvents: 'auto',
          }}
        >
          <span aria-hidden="true">i</span>
        </TooltipTrigger>
        <TooltipContent data-testid={`${triggerTestId}-content`}>{content}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
